import React, { useState, useRef, useEffect } from 'react';
import { Edit2, Check, X, Image as ImageIcon, Upload, User, Phone,
         MapPin, Search, SlidersHorizontal } from 'lucide-react';
import axios from 'axios';
import ImageCarouselModal from './ImageCarouselModal';
import ImageUploadModal from './ImageUploadModal';

const API_URL = process.env.REACT_APP_API_URL;

const ALL_COLUMNS = [
  { key: 'id',                 label: 'ID',        defaultVisible: true },
  { key: 'username',           label: 'Usuario',   defaultVisible: true },
  { key: 'recipient_name',     label: 'Nombre',    defaultVisible: true },
  { key: 'recipient_dni',      label: 'DNI',       defaultVisible: true },
  { key: 'recipient_phone',    label: 'Teléfono',  defaultVisible: true },
  { key: 'notes',              label: 'Notas',     defaultVisible: true },
  { key: 'province',           label: 'Provincia', defaultVisible: true },
  { key: 'agency_address',     label: 'Agencia',   defaultVisible: true },
  { key: 'product',            label: 'Producto',  defaultVisible: true },
  { key: 'charge',             label: 'Cargo',     defaultVisible: true },
  { key: 'deposit_advance',    label: 'Anticipo',  defaultVisible: true },
  { key: 'transport_price',    label: 'Transporte', defaultVisible: true },
  { key: 'agency_pickup_code', label: 'Código',    defaultVisible: true },
  { key: 'status',             label: 'Estado',    defaultVisible: true },
  { key: 'charged',            label: 'Cobrado',   defaultVisible: true },
  { key: 'images',             label: 'Imágenes',  defaultVisible: true },
];

function ProvinciaDeliveriesTable({ deliveries, onDeliveryUpdate, onRefresh }) {
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue]       = useState('');
  const [saving, setSaving]             = useState(false);
  const [selectedImages, setSelectedImages] = useState(null);
  const [uploadingFor, setUploadingFor]     = useState(null);
  const [searchTerm, setSearchTerm]         = useState('');
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    () => Object.fromEntries(ALL_COLUMNS.map(c => [c.key, c.defaultVisible]))
  );

  // Dual synced scrollbar refs
  const tableWrapperRef   = useRef(null);
  const topScrollbarRef   = useRef(null);
  const topScrollbarInner = useRef(null);
  const isSyncingRef      = useRef(false);

  // Keep top scrollbar width in sync with table width
  useEffect(() => {
    function updateWidth() {
      if (tableWrapperRef.current && topScrollbarInner.current) {
        const tableEl = tableWrapperRef.current.querySelector('table');
        if (tableEl) {
          topScrollbarInner.current.style.width = `${tableEl.scrollWidth}px`;
        }
      }
    }
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [deliveries, visibleColumns]);

  function onTopScroll() {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollLeft = topScrollbarRef.current.scrollLeft;
    }
    isSyncingRef.current = false;
  }

  function onBottomScroll() {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    if (topScrollbarRef.current) {
      topScrollbarRef.current.scrollLeft = tableWrapperRef.current.scrollLeft;
    }
    isSyncingRef.current = false;
  }

  const statusOptions = [
    { value: 'pending',    label: 'Pendiente', color: 'bg-orange-100 text-orange-700' },
    { value: 'processing', label: 'En Proceso', color: 'bg-blue-100 text-blue-700' },
    { value: 'agencia',    label: 'Enviado',   color: 'bg-purple-100 text-purple-700' },
    { value: 'entregado',  label: 'Entregado', color: 'bg-green-100 text-green-700' },
    { value: 'cancelado',  label: 'Cancelado', color: 'bg-red-100 text-red-700' },
  ];

  function startEdit(deliveryId, field, currentValue) {
    setEditingField({ deliveryId, field });
    setEditValue(currentValue ?? '');
  }
  function cancelEdit() { setEditingField(null); setEditValue(''); }

  function renderMoneyEdit(delivery, field, colorClass) {
    const isEditing = editingField?.deliveryId === delivery.id && editingField?.field === field;
    const value = delivery[field] || 0;

    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <input
            type="number"
            step="0.01"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            className="w-20 px-2 py-1 text-xs border-2 border-brand-primary rounded focus:outline-none font-montserrat"
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter') saveEdit(delivery.id, field);
              if (e.key === 'Escape') cancelEdit();
            }}
          />
          <button onClick={() => saveEdit(delivery.id, field)} disabled={saving} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
          <button onClick={cancelEdit} disabled={saving} className="p-1 text-red-600 hover:bg-red-50 rounded"><X className="w-4 h-4" /></button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <span className={`text-sm font-semibold ${colorClass} font-montserrat`}>
          S/ {parseFloat(value).toFixed(2)}
        </span>
        <button onClick={() => startEdit(delivery.id, field, value)} className="p-1 text-slate-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded transition-colors">
          <Edit2 className="w-3 h-3" />
        </button>
      </div>
    );
  }

  async function saveEdit(deliveryId, field) {
    setSaving(true);
    try {
      const response = await axios.patch(
        `${API_URL}/api/admin/provincia-deliveries/${deliveryId}`,
        { [field]: editValue }
      );
      onDeliveryUpdate(response.data.delivery);
      setEditingField(null);
      setEditValue('');
    } catch (error) {
      console.error('Error updating delivery:', error);
      alert('Error al actualizar. Por favor intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  }

  function getStatusLabel(status) {
    return statusOptions.find(o => o.value === status)?.label ?? status;
  }
  function getStatusColor(status) {
    return statusOptions.find(o => o.value === status)?.color ?? 'bg-slate-100 text-slate-700';
  }

  function truncateText(text, maxLength = 15) {
    if (!text) return '-';
    return text.length <= maxLength ? text : text.substring(0, maxLength) + '...';
  }

  function toggleColumn(key) {
    setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
  }

  // Filter deliveries by search term (name, phone, username, user_id, province)
  const filtered = deliveries.filter(d => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      d.recipient_name?.toLowerCase().includes(q) ||
      d.recipient_phone?.toLowerCase().includes(q) ||
      d.username?.toLowerCase().includes(q) ||
      String(d.user_id).includes(q) ||
      d.province?.toLowerCase().includes(q) ||
      d.product?.toLowerCase().includes(q)
    );
  });

  const col = (key) => visibleColumns[key]; // shorthand

  return (
    <>
      {/* Toolbar: search + column picker */}
      <div className="flex items-center gap-3 mb-3">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, tienda, provincia..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-brand-primary font-montserrat"
          />
        </div>

        {/* Column visibility toggle */}
        <div className="relative">
          <button
            onClick={() => setShowColumnPicker(p => !p)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:border-brand-primary hover:text-brand-primary transition-colors font-montserrat"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Columnas
          </button>
          {showColumnPicker && (
            <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-xl shadow-lg p-3 w-44">
              <p className="text-xs font-semibold text-slate-500 font-montserrat mb-2">Mostrar columnas</p>
              {ALL_COLUMNS.map(c => (
                <label key={c.key} className="flex items-center gap-2 py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!visibleColumns[c.key]}
                    onChange={() => toggleColumn(c.key)}
                    className="accent-brand-primary"
                  />
                  <span className="text-xs font-montserrat text-slate-700">{c.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Result count */}
        <span className="text-xs text-slate-500 font-montserrat whitespace-nowrap">
          {filtered.length} de {deliveries.length}
        </span>
      </div>

      {/* Top (mirrored) scrollbar */}
      <div
        ref={topScrollbarRef}
        onScroll={onTopScroll}
        className="overflow-x-auto overflow-y-hidden mb-0"
        style={{ height: 12 }}
      >
        <div ref={topScrollbarInner} style={{ height: 1 }} />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div
          ref={tableWrapperRef}
          onScroll={onBottomScroll}
          className="overflow-x-auto"
        >
          <table className="w-full">
            <thead className="bg-brand-primary text-white">
              <tr>
                {col('id')                 && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">ID</th>}
                {col('username')           && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Usuario</th>}
                {col('recipient_name')     && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Nombre</th>}
                {col('recipient_dni')      && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">DNI</th>}
                {col('recipient_phone')    && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Teléfono</th>}
                {col('notes')              && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Notas</th>}
                {col('province')           && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Provincia</th>}
                {col('agency_address')     && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Agencia</th>}
                {col('product')            && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Producto</th>}
                {col('charge')             && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Cargo</th>}
                {col('deposit_advance')    && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Anticipo</th>}
                {col('transport_price')    && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Transporte</th>}
                {col('agency_pickup_code') && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Código</th>}
                {col('status')             && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Estado</th>}
                {col('charged')            && <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat whitespace-nowrap">Cobrado</th>}
                {col('images')             && <th className="px-4 py-3 text-center text-xs font-semibold font-montserrat whitespace-nowrap">Imágenes</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={ALL_COLUMNS.length} className="px-4 py-8 text-center text-sm text-slate-400 font-montserrat">
                    No se encontraron entregas
                  </td>
                </tr>
              ) : filtered.map((delivery) => (
                <tr key={delivery.id} className="hover:bg-slate-50 transition-colors">
                  {col('id') && (
                    <td className="px-4 py-3 text-sm font-semibold text-slate-900 font-montserrat whitespace-nowrap">
                      #{delivery.id}
                    </td>
                  )}
                  {col('username') && (
                    <td className="px-4 py-3 text-sm text-slate-600 font-montserrat whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="font-medium">{delivery.username || `#${delivery.user_id}`}</span>
                      </div>
                    </td>
                  )}
                  {col('recipient_name') && (
                    <td className="px-4 py-3 text-sm text-slate-900 font-montserrat whitespace-nowrap">
                      {delivery.recipient_name}
                    </td>
                  )}
                  {col('recipient_dni') && (
                    <td className="px-4 py-3 text-sm text-slate-600 font-montserrat whitespace-nowrap">
                      {delivery.recipient_dni || '-'}
                    </td>
                  )}
                  {col('recipient_phone') && (
                    <td className="px-4 py-3 text-sm text-slate-600 font-montserrat whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                        {delivery.recipient_phone}
                      </div>
                    </td>
                  )}
                  {col('notes') && (
                    <td className="px-4 py-3 text-sm text-slate-600 font-montserrat">
                      <span title={delivery.notes}>{truncateText(delivery.notes, 15)}</span>
                    </td>
                  )}
                  {col('province') && (
                    <td className="px-4 py-3 text-sm font-montserrat whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
                        <MapPin className="w-3 h-3" />
                        {delivery.province}
                      </span>
                    </td>
                  )}
                  {col('agency_address') && (
                    <td className="px-4 py-3 text-sm text-slate-600 font-montserrat">
                      <span title={delivery.agency_address}>{truncateText(delivery.agency_address, 20)}</span>
                    </td>
                  )}
                  {col('product') && (
                    <td className="px-4 py-3 text-sm text-slate-600 font-montserrat">
                      <span title={delivery.product}>{truncateText(delivery.product, 20)}</span>
                    </td>
                  )}
                  {col('charge') && (
                    <td className="px-4 py-3 text-sm font-semibold text-slate-900 font-montserrat whitespace-nowrap">
                      S/ {parseFloat(delivery.charge || 0).toFixed(2)}
                    </td>
                  )}
                  {col('deposit_advance') && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      {renderMoneyEdit(delivery, 'deposit_advance', 'text-blue-600')}
                    </td>
                  )}
                  {col('transport_price') && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      {renderMoneyEdit(delivery, 'transport_price', 'text-amber-600')}
                    </td>
                  )}
                  {col('agency_pickup_code') && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingField?.deliveryId === delivery.id && editingField?.field === 'agency_pickup_code' ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            className="w-24 px-2 py-1 text-xs border-2 border-brand-primary rounded focus:outline-none font-montserrat"
                            autoFocus
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveEdit(delivery.id, 'agency_pickup_code');
                              if (e.key === 'Escape') cancelEdit();
                            }}
                          />
                          <button onClick={() => saveEdit(delivery.id, 'agency_pickup_code')} disabled={saving} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                          <button onClick={cancelEdit} disabled={saving} className="p-1 text-red-600 hover:bg-red-50 rounded"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-900 font-montserrat">{delivery.agency_pickup_code || '-'}</span>
                          <button onClick={() => startEdit(delivery.id, 'agency_pickup_code', delivery.agency_pickup_code)} className="p-1 text-slate-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded transition-colors">
                            <Edit2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                  {col('status') && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingField?.deliveryId === delivery.id && editingField?.field === 'status' ? (
                        <div className="flex items-center gap-1">
                          <select
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            className="text-xs border-2 border-brand-primary rounded focus:outline-none font-montserrat px-2 py-1"
                            autoFocus
                          >
                            {statusOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                          <button onClick={() => saveEdit(delivery.id, 'status')} disabled={saving} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                          <button onClick={cancelEdit} disabled={saving} className="p-1 text-red-600 hover:bg-red-50 rounded"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold font-montserrat ${getStatusColor(delivery.status)}`}>
                            {getStatusLabel(delivery.status)}
                          </span>
                          <button onClick={() => startEdit(delivery.id, 'status', delivery.status)} className="p-1 text-slate-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded transition-colors">
                            <Edit2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                  {col('charged') && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      {renderMoneyEdit(delivery, 'charged', 'text-green-600')}
                    </td>
                  )}
                  {col('images') && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setSelectedImages({ images: delivery.images || [], deliveryId: delivery.id, recipientName: delivery.recipient_name })}
                          disabled={!delivery.images || delivery.images.length === 0}
                          className="p-2 text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative"
                          title="Ver imágenes"
                        >
                          <ImageIcon className="w-4 h-4" />
                          {delivery.images?.length > 0 && (
                            <span className="absolute -top-1 -right-1 bg-brand-primary text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-montserrat">
                              {delivery.images.length}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => setUploadingFor(delivery)}
                          className="p-2 text-brand-gold hover:bg-brand-gold/10 rounded-lg transition-colors"
                          title="Subir imagen"
                        >
                          <Upload className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedImages && (
        <ImageCarouselModal
          images={selectedImages.images}
          deliveryId={selectedImages.deliveryId}
          recipientName={selectedImages.recipientName}
          onClose={() => setSelectedImages(null)}
        />
      )}
      {uploadingFor && (
        <ImageUploadModal
          delivery={uploadingFor}
          onClose={() => setUploadingFor(null)}
          onUploadComplete={() => { setUploadingFor(null); onRefresh(); }}
        />
      )}
    </>
  );
}

export default ProvinciaDeliveriesTable;
